"use strict";
// packages/rdti-schema/src/versions/fy25.ts
//
// FY25 (2024-25 income year) schema. Reflects the AusIndustry registration
// form effective 15 Aug 2025 — Projects Q46–Q60, Core activities Q61–Q93,
// Supporting activities Q94–Q114. Character caps, minimum floors, and skip
// logic are taken from the official "RDTI registration application form
// questions" PDF (see `source` below).
Object.defineProperty(exports, "__esModule", { value: true });
exports.FY25 = void 0;
const YES_NO = [
    { value: 'yes', label: 'Yes' },
    { value: 'no', label: 'No' },
];
exports.FY25 = {
    version: 'FY25',
    effectiveFrom: '2024-07-01',
    source: 'ausindustry-rdti-registration-application-form-questions-pdf (effective 2025-08-15)',
    project: [
        {
            key: 'project.influence',
            questionNumber: 46,
            label: 'How much did the R&D Tax Incentive influence the business to do the R&D?',
            inputType: 'dropdown',
            options: [
                { value: 'fully', label: 'Fully — we would not have done it without the incentive' },
                { value: 'significantly', label: 'Significantly — accelerated or expanded scope' },
                { value: 'partially', label: 'Partially — affected timing or method' },
                { value: 'minimally', label: 'Minimally — we would have done it anyway' },
            ],
            required: true,
        },
        {
            key: 'project.name',
            questionNumber: 47,
            label: 'Name for this project',
            inputType: 'text',
            maxChars: 200,
            required: true,
        },
        {
            key: 'project.referenceDescription',
            questionNumber: 48,
            label: 'Project reference description (optional)',
            inputType: 'text',
            maxChars: 250,
            required: false,
        },
        {
            key: 'project.hasAdvanceFinding',
            questionNumber: 49,
            label: 'Does this project include activities covered by an advance or overseas finding?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'project.findingCertificateNumbers',
            questionNumber: 50,
            label: 'What are the certificate reference numbers?',
            inputType: 'text',
            maxChars: 100,
            required: true,
            conditionalOn: { field: 'project.hasAdvanceFinding', equals: 'yes' },
        },
        {
            key: 'project.duration',
            questionNumber: 51,
            label: 'What is the expected duration of this project?',
            inputType: 'dateRange',
            required: true,
        },
        {
            key: 'project.totalEstimatedExpenditure',
            questionNumber: 52,
            label: 'Total estimated expenditure for the project',
            inputType: 'currency',
            required: true,
        },
        {
            key: 'project.objectives',
            questionNumber: 53,
            label: 'What are the objectives of this project?',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: ['measurable', 'objective', 'demonstrate', 'achieve', 'knowledge', 'outcome'],
                redFlags: ['sales', 'revenue', 'market share', 'commercial launch'],
                weight: 0.1,
            },
        },
        {
            key: 'project.documentsKept',
            questionNumber: 54,
            label: 'Describe what documents you have kept, or will keep',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: [
                    'lab notebooks',
                    'test results',
                    'experimental data',
                    'design documents',
                    'meeting minutes',
                    'version control',
                    'contemporaneous',
                ],
                redFlags: ['not documented', 'no records', 'verbal only', 'no formal records'],
                weight: 0.08,
            },
        },
        {
            key: 'project.locationAtMainAddress',
            questionNumber: 55,
            label: 'Is the majority of R&D activities at the main business address?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'project.address',
            questionNumber: 56,
            label: 'Address of majority R&D location',
            inputType: 'addressLookup',
            required: true,
            conditionalOn: { field: 'project.locationAtMainAddress', equals: 'no' },
        },
        {
            key: 'project.plantAndFacilities',
            questionNumber: 57,
            label: 'Briefly describe the plant and facilities to be used',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: ['laboratory', 'equipment', 'instrument', 'facility', 'prototype', 'test rig'],
                redFlags: ['off-the-shelf', 'standard office equipment', 'general purpose'],
                weight: 0.05,
            },
        },
        {
            key: 'project.feedstockExpenditure',
            questionNumber: 58,
            label: 'For the selected income period, what was the feedstock expenditure?',
            inputType: 'currency',
            required: true,
        },
        {
            key: 'project.fieldOfResearchPrimary',
            questionNumber: 59,
            label: 'Primary field of research',
            inputType: 'dropdown',
            options: [], // ANZSRC FoR codes, loaded at runtime
            required: true,
        },
        {
            key: 'project.fieldOfResearchSecondary',
            questionNumber: 59,
            label: 'Secondary field of research',
            inputType: 'dropdown',
            options: [],
            required: false,
        },
        {
            key: 'project.beneficialOwnership',
            questionNumber: 60,
            label: 'Will the company be a beneficial owner of the results / IP?',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: [
                    'beneficial owner',
                    'on own behalf',
                    'bears the financial risk',
                    'controls the activities',
                    'effective ownership',
                    'IP retained',
                ],
                redFlags: ['client owns', 'customer owns', 'work for hire', 'subcontracted to us', 'commissioned by'],
                weight: 0.12,
            },
        },
    ],
    coreActivity: [
        {
            key: 'core.isPreviouslyRegistered',
            questionNumber: 61,
            label: 'Is this a previously registered core activity?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.name',
            questionNumber: 62,
            label: 'Name for this core R&D activity',
            inputType: 'text',
            maxChars: 250,
            required: true,
        },
        {
            key: 'core.description',
            questionNumber: 63,
            label: 'Describe the core R&D activity',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: ['experiment', 'test', 'investigate', 'observe', 'measure', 'systematic'],
                redFlags: ['routine', 'standard practice', 'business as usual', 'customer requirement'],
                weight: 0.1,
            },
        },
        {
            key: 'core.projectId',
            questionNumber: 64,
            label: 'Which project is this core R&D activity under?',
            inputType: 'dropdown',
            options: [], // sibling projects, loaded at runtime
            required: true,
        },
        {
            key: 'core.isExcludedCategory',
            questionNumber: 65,
            label: 'Does this activity fall into one of the excluded categories?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.performedFor',
            questionNumber: 66,
            label: 'Who was this core R&D activity performed for?',
            inputType: 'radio',
            options: [
                { value: 'self', label: 'The company itself' },
                { value: 'other', label: 'Another entity' },
            ],
            required: true,
        },
        {
            key: 'core.performingCompanyName',
            questionNumber: 67,
            label: 'Name of company performing this core R&D',
            inputType: 'text',
            maxChars: 250,
            required: true,
            conditionalOn: { field: 'core.performedFor', equals: 'other' },
        },
        {
            key: 'core.isAssociateAffiliate',
            questionNumber: 68,
            label: 'Is the conducting entity an associate / affiliate?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.conductedOverseas',
            questionNumber: 69,
            label: 'Is the core R&D activity being conducted overseas?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.isConsolidatedGroup',
            questionNumber: 70,
            label: 'Conducted as part of a consolidated group / MEC / subsidiary?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.conductingEntityAbn',
            questionNumber: 71,
            label: 'ABN / ACN / ARBN of the conducting entity',
            inputType: 'abnLookup',
            required: true,
            conditionalOn: { field: 'core.performedFor', equals: 'other' },
        },
        {
            key: 'core.conductingCompanyCountry',
            questionNumber: 72,
            label: 'Country of residence for company',
            inputType: 'dropdown',
            options: [],
            required: true,
        },
        {
            key: 'core.commencesAfterSupporting',
            questionNumber: 73,
            label: 'Does this core R&D activity commence after the supporting activity?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.dates',
            questionNumber: 74,
            label: 'Actual or anticipated start and end dates',
            inputType: 'dateRange',
            required: true,
        },
        {
            key: 'core.overseasOrRspOrCrc',
            questionNumber: 75,
            label: 'Was some or all of this activity overseas / RSP / CRC?',
            inputType: 'radio',
            options: [
                { value: 'overseas', label: 'Overseas' },
                { value: 'rsp', label: 'Research Service Provider' },
                { value: 'crc', label: 'Cooperative Research Centre' },
                { value: 'none', label: 'None of the above' },
            ],
            required: true,
        },
        {
            key: 'core.hasDetermination',
            questionNumber: 76,
            label: 'Is this core R&D activity covered by an R&DTI determination?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.determinationReliedOn',
            questionNumber: 77,
            label: 'Which determination are you relying on?',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            conditionalOn: { field: 'core.hasDetermination', equals: 'yes' },
        },
        {
            key: 'core.unknownOutcomeBasis',
            questionNumber: 78,
            label: 'How did the company determine that the outcome was unknown?',
            inputType: 'checkbox',
            options: [
                { value: 'literature', label: 'Literature search' },
                { value: 'patent', label: 'Patent search' },
                { value: 'competent_professional', label: 'Consultation with competent professional' },
                { value: 'prior_experiments', label: 'Prior internal experiments' },
                { value: 'industry_consultation', label: 'Industry consultation' },
                { value: 'other', label: 'Other' },
            ],
            required: true,
        },
        {
            key: 'core.sourcesInvestigated',
            questionNumber: 79,
            label: 'Please explain what sources were investigated',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: ['literature', 'patent', 'journal', 'database', 'IEEE', 'arxiv', 'industry standard'],
                redFlags: ['no research', 'did not search', 'skipped', 'not applicable'],
                weight: 0.1,
            },
        },
        {
            key: 'core.competentProfessional',
            questionNumber: 80,
            label: "Why couldn't a competent professional know or determine the outcome in advance?",
            inputType: 'richText',
            maxChars: 4000,
            minChars: 600,
            required: true,
            complianceRules: {
                keywords: [
                    'competent professional',
                    'cannot determine',
                    'uncertain',
                    'unpredictable',
                    'novel',
                    'no established',
                    'state of the art',
                ],
                redFlags: ['routine', 'standard practice', 'well-known', 'established method', 'textbook'],
                weight: 0.18,
            },
        },
        {
            key: 'core.whyNoSearch',
            questionNumber: 81,
            label: 'Explain why the company did not search for an existing solution',
            inputType: 'richText',
            maxChars: 1000,
            required: true,
            conditionalOn: { field: 'core.unknownOutcomeBasis', equals: 'other' },
        },
        {
            key: 'core.hypothesis',
            questionNumber: 82,
            label: 'What is the hypothesis?',
            inputType: 'richText',
            maxChars: 4000,
            minChars: 650,
            required: true,
            complianceRules: {
                keywords: ['hypothesis', 'we expect', 'if', 'then', 'because', 'predict', 'will result in'],
                redFlags: ['trial and error', 'trial-and-error', 'guess', 'see what happens'],
                weight: 0.18,
            },
        },
        {
            key: 'core.experiment',
            questionNumber: 83,
            label: 'What is the experiment and how does it test the hypothesis?',
            inputType: 'richText',
            maxChars: 4000,
            minChars: 650,
            required: true,
            complianceRules: {
                keywords: [
                    'experimental design',
                    'controlled',
                    'methodology',
                    'systematic',
                    'data collection',
                    'measurement',
                    'variable',
                    'test',
                ],
                redFlags: ['informal testing', 'trial and error', 'ad hoc', 'random'],
                weight: 0.18,
            },
        },
        {
            key: 'core.evaluation',
            questionNumber: 84,
            label: 'How did you evaluate (or plan to evaluate) the results?',
            inputType: 'richText',
            maxChars: 4000,
            minChars: 530,
            required: true,
            complianceRules: {
                keywords: ['analyse', 'compare', 'statistical', 'success criteria', 'observable', 'measurable', 'benchmark'],
                redFlags: ['gut feel', 'looks right', 'business metrics', 'sales figures'],
                weight: 0.1,
            },
        },
        {
            key: 'core.conclusions',
            questionNumber: 85,
            label: 'If you reached conclusions, what were they?',
            inputType: 'richText',
            maxChars: 4000,
            minChars: 340,
            required: false,
            complianceRules: {
                keywords: ['concluded', 'finding', 'result', 'observation', 'data showed', 'demonstrated'],
                redFlags: ['no conclusion', 'inconclusive without analysis'],
                weight: 0.08,
            },
        },
        {
            key: 'core.evidenceKept',
            questionNumber: 86,
            label: 'What evidence did the company keep about the activity?',
            inputType: 'checkbox',
            options: [
                { value: 'lab_notebooks', label: 'Lab notebooks' },
                { value: 'test_data', label: 'Test data / results' },
                { value: 'design_docs', label: 'Design documents' },
                { value: 'source_code', label: 'Source code / version control' },
                { value: 'meeting_minutes', label: 'Meeting minutes' },
                { value: 'reports', label: 'Evaluation reports' },
                { value: 'photos_video', label: 'Photos / video' },
                { value: 'other', label: 'Other' },
            ],
            required: true,
        },
        {
            key: 'core.evidenceOther',
            questionNumber: 87,
            label: 'Describe the other evidence',
            inputType: 'text',
            maxChars: 100,
            required: true,
            conditionalOn: { field: 'core.evidenceKept', equals: 'other' },
        },
        {
            key: 'core.forNewKnowledgePurpose',
            questionNumber: 88,
            label: 'Did you conduct this for the purpose of generating new knowledge?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'core.newKnowledge',
            questionNumber: 89,
            label: 'What new knowledge was (or will be) generated?',
            inputType: 'richText',
            maxChars: 4000,
            minChars: 600,
            required: true,
            complianceRules: {
                keywords: ['new knowledge', 'novel', 'previously unknown', 'discover', 'advance', 'first time'],
                redFlags: ['incremental', 'minor improvement', 'already known internally'],
                weight: 0.15,
            },
        },
        {
            key: 'core.rspId',
            questionNumber: 90,
            label: 'Which Research Service Provider conducted the activity?',
            inputType: 'dropdown',
            options: [],
            required: true,
            conditionalOn: { field: 'core.overseasOrRspOrCrc', equals: 'rsp' },
        },
        {
            key: 'core.rspDescription',
            questionNumber: 90,
            label: 'Describe the RSP engagement',
            inputType: 'richText',
            maxChars: 2000,
            required: true,
            conditionalOn: { field: 'core.overseasOrRspOrCrc', equals: 'rsp' },
        },
        {
            key: 'core.crcId',
            questionNumber: 91,
            label: 'Which Cooperative Research Centre?',
            inputType: 'dropdown',
            options: [],
            required: true,
            conditionalOn: { field: 'core.overseasOrRspOrCrc', equals: 'crc' },
        },
        {
            key: 'core.crcDescription',
            questionNumber: 91,
            label: 'Describe the CRC engagement',
            inputType: 'richText',
            maxChars: 2000,
            minChars: 50,
            required: true,
            conditionalOn: { field: 'core.overseasOrRspOrCrc', equals: 'crc' },
        },
        {
            key: 'core.expenditureSelectedYear',
            questionNumber: 92,
            label: 'For the selected income period, what was the expenditure?',
            inputType: 'currency',
            required: true,
        },
        {
            key: 'core.expenditureMultiYear',
            questionNumber: 93,
            label: 'Total actual and reasonably anticipated expenditure across years',
            inputType: 'multiCurrency',
            required: true,
        },
    ],
    supportingActivity: [
        {
            key: 'support.isPreviouslyRegistered',
            questionNumber: 94,
            label: 'Is this a previously registered supporting activity?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'support.name',
            questionNumber: 95,
            label: 'Name for this supporting R&D activity',
            inputType: 'text',
            maxChars: 200,
            required: true,
        },
        {
            key: 'support.description',
            questionNumber: 96,
            label: 'Briefly describe this supporting R&D activity',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: ['support', 'enables', 'required for', 'integral to'],
                redFlags: ['general business', 'administrative', 'routine operations'],
                weight: 0.1,
            },
        },
        {
            key: 'support.overseasOrRspOrCrc',
            questionNumber: 97,
            label: 'Was some or all of this activity overseas / RSP / CRC?',
            inputType: 'radio',
            options: [
                { value: 'overseas', label: 'Overseas' },
                { value: 'rsp', label: 'Research Service Provider' },
                { value: 'crc', label: 'Cooperative Research Centre' },
                { value: 'none', label: 'None of the above' },
            ],
            required: true,
        },
        {
            key: 'support.relatedCoreActivityIds',
            questionNumber: 98,
            label: 'Which core R&D activities are supported by this activity?',
            inputType: 'checkbox',
            options: [], // populated at runtime from sibling core activities
            required: true,
        },
        {
            key: 'support.howSupportsCore',
            questionNumber: 99,
            label: 'How does this supporting activity directly support / relate to the core?',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: [
                    'directly supports',
                    'enables core R&D',
                    'necessary for',
                    'facilitates testing',
                    'provides input to',
                    'integral to the experiment',
                ],
                redFlags: ['general business support', 'administrative', 'routine operations', 'standard IT'],
                weight: 0.15,
            },
        },
        {
            key: 'support.performedFor',
            questionNumber: 100,
            label: 'Who will this supporting R&D activity be performed for?',
            inputType: 'radio',
            options: [
                { value: 'self', label: 'The company itself' },
                { value: 'other', label: 'Another entity' },
            ],
            required: true,
        },
        {
            key: 'support.performingEntityName',
            questionNumber: 101,
            label: 'Name of R&D entity performing this supporting activity',
            inputType: 'text',
            maxChars: 250,
            required: true,
            conditionalOn: { field: 'support.performedFor', equals: 'other' },
        },
        {
            key: 'support.isAssociateAffiliate',
            questionNumber: 102,
            label: 'Is the conducting entity an associate / affiliate?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'support.conductedOverseas',
            questionNumber: 103,
            label: 'Is the supporting R&D activity being conducted overseas?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'support.isConsolidatedGroup',
            questionNumber: 104,
            label: 'Conducted as part of a consolidated group / MEC / subsidiary?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'support.conductingEntityAbn',
            questionNumber: 105,
            label: 'ABN / ACN / ARBN of the entity',
            inputType: 'abnLookup',
            required: true,
            conditionalOn: { field: 'support.performedFor', equals: 'other' },
        },
        {
            key: 'support.rspId',
            questionNumber: 106,
            label: 'Which Research Service Provider conducted the activity?',
            inputType: 'dropdown',
            options: [],
            required: true,
            conditionalOn: { field: 'support.overseasOrRspOrCrc', equals: 'rsp' },
        },
        {
            key: 'support.rspDescription',
            questionNumber: 106,
            label: 'Describe the RSP engagement',
            inputType: 'richText',
            maxChars: 2000,
            required: true,
            conditionalOn: { field: 'support.overseasOrRspOrCrc', equals: 'rsp' },
        },
        {
            key: 'support.crcId',
            questionNumber: 107,
            label: 'Which Cooperative Research Centre?',
            inputType: 'dropdown',
            options: [],
            required: true,
            conditionalOn: { field: 'support.overseasOrRspOrCrc', equals: 'crc' },
        },
        {
            key: 'support.crcDescription',
            questionNumber: 107,
            label: 'Describe the CRC engagement',
            inputType: 'richText',
            maxChars: 2000,
            required: true,
            conditionalOn: { field: 'support.overseasOrRspOrCrc', equals: 'crc' },
        },
        {
            key: 'support.dates',
            questionNumber: 108,
            label: 'When was this supporting R&D activity start / end?',
            inputType: 'dateRange',
            required: true,
        },
        {
            key: 'support.expenditureSelectedYear',
            questionNumber: 109,
            label: 'For the selected income period, what was the expenditure?',
            inputType: 'currency',
            required: true,
        },
        {
            key: 'support.isExcludedCategory',
            questionNumber: 110,
            label: 'Does this supporting R&D activity fall into excluded categories?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'support.producesTangibleProduct',
            questionNumber: 111,
            label: 'Did this supporting R&D activity produce a tangible product?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'support.dominantPurposeMet',
            questionNumber: 112,
            label: 'Was this for the dominant purpose of supporting core R&D?',
            inputType: 'radio',
            options: YES_NO,
            required: true,
        },
        {
            key: 'support.dominantPurpose',
            questionNumber: 113,
            label: 'Explain why and how this satisfies the dominant purpose test',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: [
                    'dominant purpose',
                    'prevailing purpose',
                    'primarily for R&D',
                    'main purpose',
                    'ruling purpose',
                    'would not exist but for',
                ],
                redFlags: ['also used for', 'sometimes supports', 'general purpose', 'dual purpose', 'commercial production'],
                weight: 0.15,
            },
        },
        {
            key: 'support.evidence',
            questionNumber: 114,
            label: 'What evidence did the company keep about the supporting activity?',
            inputType: 'richText',
            maxChars: 4000,
            required: true,
            complianceRules: {
                keywords: ['documentation', 'records', 'logs', 'test results', 'design docs', 'meeting minutes'],
                redFlags: ['not documented', 'no records', 'verbal only'],
                weight: 0.08,
            },
        },
    ],
};
